/** EGB-00005 — Enterprise Product Governance Framework (mirrors pkg/productgovernance) */

export const EGB_PROD_ID = 'EGB-00005';

export interface CouncilMemberRow {
  role: string;
  responsibility: string;
  decisionRights: string;
  reviewCycle: string;
}

export interface VisionRow {
  area: string;
  value: string;
  status: string;
}

export interface PortfolioLifecycleRow {
  category: string;
  item: string;
  status: string;
  count: number;
}

export interface FeatureGovernanceRow {
  requirement: string;
  status: string;
  score: number;
}

export interface RoadmapRow {
  roadmap: string;
  progress: number;
  status: string;
}

export interface InnovationDiscoveryRow {
  area: string;
  detail: string;
  status: string;
  count: number;
}

export interface PrioritizationRow {
  framework: string;
  value: string;
  status: string;
  score: number;
}

export interface ExecutiveDashboardRow {
  metric: string;
  value: string;
  status: string;
  score: number;
}

export const COUNCIL_ROLES = [
  'Chief Product Officer', 'Chief Executive Officer', 'Chief Enterprise Architect', 'Chief AI Officer',
  'Customer Success Director', 'Engineering Director', 'Marketing Director', 'Sales Director',
  'Finance Representative', 'Accessibility Specialist',
];

export const COUNCIL_FUNCTIONS = ['Roles', 'Responsibilities', 'Decision Rights', 'Approval Matrix', 'Investment Governance', 'Review Cycles'];

export const PRODUCT_VISION = [
  'Mission', 'Vision', 'North Star Metric', 'Strategic Objectives', 'Target Customers', 'Target Markets', 'Competitive Position',
];

export const PORTFOLIO_AREAS = [
  'Core Platform', 'AI Platform', 'Marketplace Platform', 'Enterprise Features', 'Mobile Platform', 'Extensions', 'Partner Products', 'Innovation Portfolio',
];

export const LIFECYCLE_STAGES = [
  'Idea', 'Discovery', 'Research', 'Validation', 'Business Case', 'Architecture Review', 'Development',
  'Alpha', 'Beta', 'General Availability', 'Maintenance', 'Deprecation', 'Retirement',
];

export const FEATURE_GOVERNANCE = [
  'Business Problem', 'Customer Problem', 'Business Value', 'Expected ROI', 'Dependencies',
  'Acceptance Criteria', 'KPIs', 'Success Metrics', 'Owner', 'Retirement Strategy',
];

export const ROADMAP_TYPES = [
  'Strategic Roadmap', 'Quarterly Roadmap', 'Release Roadmap', 'AI Roadmap', 'Marketplace Roadmap', 'Technology Roadmap',
];

export const INNOVATION_AREAS = [
  'Idea Collection', 'Research', 'Proof of Concept', 'Prototype', 'Validation', 'Investment Decisions', 'Commercialization', 'Knowledge Capture',
];

export const CUSTOMER_FEEDBACK = [
  'Ideas', 'Votes', 'Customer Interviews', 'Support Trends', 'NPS', 'CSAT', 'Marketplace Feedback', 'AI Feedback',
];

export const PRODUCT_DISCOVERY = [
  'Market Research', 'Competitive Analysis', 'User Research', 'Problem Discovery', 'Opportunity Mapping', 'Business Case Development',
];

export const PRIORITIZATION = [
  'Weighted Scoring', 'RICE', 'WSJF', 'Business Value', 'Customer Impact', 'Engineering Effort', 'Strategic Alignment', 'AI Recommendations',
];

export const BUSINESS_VALUE = [
  'Revenue Impact', 'Profit Impact', 'Retention', 'Acquisition', 'Operational Efficiency', 'Marketplace Growth', 'AI Value',
];

export const PRODUCT_ANALYTICS = [
  'Usage', 'Adoption', 'Retention', 'Feature Success', 'Workflow Adoption', 'Marketplace Usage', 'Business Outcomes',
];

export const EXPERIMENTATION = [
  'Feature Flags', 'A/B Testing', 'Canary Releases', 'Pilot Programs', 'AI Experiments', 'Marketplace Experiments',
];

export const PRODUCT_KPIS = [
  'North Star Metric', 'DAU', 'WAU', 'MAU', 'Retention', 'Churn', 'Expansion', 'MRR', 'ARR', 'Customer Success',
];

export const RELEASE_PLANNING = [
  'Quarterly Planning', 'Epic Planning', 'Capacity Planning', 'Dependency Planning', 'Risk Planning', 'Launch Planning',
];

export const DEPRECATION_GOVERNANCE = ['Feature Sunset', 'Migration', 'Customer Communication', 'Archive', 'Retirement'];

export const DOCUMENTATION = [
  'Vision Documents', 'Strategy Documents', 'Business Cases', 'Research Reports', 'Decision Logs', 'Roadmaps', 'Release Notes',
];

export const EXECUTIVE_DASHBOARD = [
  'Product Health', 'Roadmap Progress', 'Innovation Score', 'Customer Satisfaction', 'Investment Efficiency', 'Business Value', 'Risk', 'ROI',
];

export const GOVERNANCE_ANALYTICS = [
  'Roadmap Predictability', 'Innovation Velocity', 'Feature Success', 'Investment ROI', 'Customer Value', 'Delivery Performance',
];

export const ACCESSIBILITY = ['Inclusive Product Design', 'Accessibility Reviews', 'Localization Readiness'];

export const ARCHITECTURE_STAGES = [
  'Product Council', 'Product Vision', 'Portfolio & Lifecycle', 'Feature Governance',
  'Roadmap Governance', 'Innovation & Discovery', 'Prioritization & Analytics', 'Executive Dashboard',
];

export const LIVE_ROUTES: Record<string, string> = {
  masterConstitution: '/platform/master-constitution',
  architectureGovernance: '/platform/architecture-governance',
  growthPlanner: '/strategy/growth-planner',
  analytics: '/analytics',
};

export const DEMO_COUNCIL: CouncilMemberRow[] = [
  { role: 'Chief Product Officer', responsibility: 'Product strategy & portfolio governance', decisionRights: 'Final product approval', reviewCycle: 'Weekly' },
  { role: 'Chief Executive Officer', responsibility: 'Strategic alignment & investment decisions', decisionRights: 'Executive sign-off', reviewCycle: 'Monthly' },
  { role: 'Chief Enterprise Architect', responsibility: 'Architecture sustainability review', decisionRights: 'Architecture gate veto', reviewCycle: 'Bi-weekly' },
  { role: 'Chief AI Officer', responsibility: 'AI-first product strategy', decisionRights: 'AI roadmap approval', reviewCycle: 'Weekly' },
  { role: 'Customer Success Director', responsibility: 'Customer feedback & NPS governance', decisionRights: 'Customer impact sign-off', reviewCycle: 'Weekly' },
  { role: 'Finance Representative', responsibility: 'ROI & business value validation', decisionRights: 'Investment approval', reviewCycle: 'Monthly' },
];

export const DEMO_VISION: VisionRow[] = [
  { area: 'Mission', value: 'Autonomous AI commerce operating system for enterprise sellers', status: 'active' },
  { area: 'North Star', value: 'Revenue per seller per month', status: 'tracked' },
  { area: 'Strategic Objective', value: 'Marketplace-independent AI-first platform', status: 'on_track' },
  { area: 'Target Market', value: 'Enterprise Amazon sellers + multi-marketplace expansion', status: 'active' },
  { area: 'Competitive Position', value: 'AI-native vs. legacy commerce tools', status: 'leading' },
];

export const DEMO_PORTFOLIO_LIFECYCLE: PortfolioLifecycleRow[] = [
  { category: 'Portfolio', item: 'Core Platform', status: 'ga', count: 12 },
  { category: 'Portfolio', item: 'AI Platform', status: 'ga', count: 8 },
  { category: 'Portfolio', item: 'Marketplace Platform', status: 'ga', count: 6 },
  { category: 'Portfolio', item: 'Innovation Portfolio', status: 'active', count: 14 },
  { category: 'Lifecycle', item: 'In Development', status: 'active', count: 23 },
  { category: 'Lifecycle', item: 'In Beta', status: 'active', count: 5 },
  { category: 'Lifecycle', item: 'In Deprecation', status: 'planned', count: 2 },
];

export const DEMO_FEATURE_GOVERNANCE: FeatureGovernanceRow[] = [
  { requirement: 'Business Problem Defined', status: 'required', score: 100 },
  { requirement: 'Customer Problem Validated', status: 'required', score: 98 },
  { requirement: 'Expected ROI Documented', status: 'required', score: 95 },
  { requirement: 'Acceptance Criteria', status: 'required', score: 100 },
  { requirement: 'Retirement Strategy', status: 'required', score: 88 },
];

export const DEMO_ROADMAP: RoadmapRow[] = [
  { roadmap: 'Strategic Roadmap', progress: 72, status: 'on_track' },
  { roadmap: 'Quarterly Roadmap Q3', progress: 65, status: 'on_track' },
  { roadmap: 'AI Roadmap', progress: 80, status: 'ahead' },
  { roadmap: 'Marketplace Roadmap', progress: 58, status: 'on_track' },
  { roadmap: 'Technology Roadmap', progress: 70, status: 'on_track' },
];

export const DEMO_INNOVATION_DISCOVERY: InnovationDiscoveryRow[] = [
  { area: 'Innovation', detail: 'Active POCs', status: 'active', count: 6 },
  { area: 'Innovation', detail: 'Prototypes in validation', status: 'active', count: 4 },
  { area: 'Discovery', detail: 'Market research reports', status: 'current', count: 12 },
  { area: 'Discovery', detail: 'Competitive analyses', status: 'current', count: 8 },
  { area: 'Feedback', detail: 'Customer ideas collected', status: 'active', count: 156 },
  { area: 'Feedback', detail: 'NPS score', status: 'healthy', count: 62 },
];

export const DEMO_PRIORITIZATION: PrioritizationRow[] = [
  { framework: 'RICE', value: 'Primary scoring model', status: 'active', score: 95 },
  { framework: 'WSJF', value: 'Release prioritization', status: 'active', score: 92 },
  { framework: 'AI Recommendations', value: 'Copilot-assisted ranking', status: 'active', score: 88 },
  { framework: 'Feature Adoption', value: '78% avg adoption', status: 'healthy', score: 78 },
  { framework: 'A/B Experiments', value: '12 active experiments', status: 'active', score: 85 },
  { framework: 'Marketplace Usage', value: 'Amazon 94% · expanding', status: 'growing', score: 94 },
];

export const DEMO_EXECUTIVE: ExecutiveDashboardRow[] = [
  { metric: 'Product Health', value: 'A', status: 'healthy', score: 91 },
  { metric: 'Roadmap Progress', value: '68%', status: 'on_track', score: 68 },
  { metric: 'Innovation Score', value: '82', status: 'strong', score: 82 },
  { metric: 'Customer Satisfaction', value: 'NPS 62', status: 'healthy', score: 62 },
  { metric: 'Investment ROI', value: '3.2x', status: 'positive', score: 88 },
  { metric: 'Delivery Performance', value: '92% predictability', status: 'on_track', score: 92 },
];

export function buildProductDashboard() {
  return {
    council_roles: COUNCIL_ROLES.length,
    portfolio_areas: PORTFOLIO_AREAS.length,
    lifecycle_stages: LIFECYCLE_STAGES.length,
    roadmap_types: ROADMAP_TYPES.length,
    product_kpis: PRODUCT_KPIS.length,
    governance_areas: FEATURE_GOVERNANCE.length + INNOVATION_AREAS.length,
  };
}
